import {
  QueueMessageType,
  TriggerKey,
  UserCommunicationAgentQueueBodyBase,
} from '@this+that/models';

import { DoBoxEntryKind, InboxEntryPriority } from '../inboxEntries';

export interface GenerateDoBoxStepConfig {
  prompt: string;
  taskGroupId?: string;
  /** When true, generated DoBoxes are created with the suggestion flag set,
   * so they show in the suggestions UI rather than the active inbox. Mirrors
   * the behavior of the PROCESS_DOBOX flow. Defaults to false. */
  isSuggestion?: boolean;
}

export interface LoadDoBoxStepConfig {
  statusFilter?: string;
  /** Keep only entries updated within this many days (stringified number).
   * Omitted = no time window. */
  daysBack?: string;
  taskGroupIds?: string[];
  /** Sort direction by updatedAt. 'ASC' = oldest-updated first, 'DESC' =
   * most-recently-updated first. Case-insensitive. Defaults to 'DESC'. */
  sortOrder?: string;
  /** Max number of entries to return after filtering + sorting. Stringified
   * number (config values are stored as strings). Omitted/invalid = the hard
   * cap MAX_FLOW_ITEM_LIMIT (50), which is also the maximum. */
  limit?: string;
  /** When true, loads ALL entries in the requested task group(s) regardless
   * of per-entry participation, for team-wide sweeps. Still gated by the
   * caller's task-group access — only removes the per-entry participant
   * filter. Defaults to false (current per-entry participant filtering). */
  includeAll?: boolean;
}

export interface AppendToDoBoxStepConfig {
  inboxEntryId: string;
  communicationIds: string;
}

export interface CheckAndAppendDoBoxStepConfig {
  includeResolved?: boolean;
  resolvedDaysBack?: string;
  /** @deprecated Auto-detected from previous LOAD step outputs */
  communicationsSource?: string;
  /** Set by the system flows only: link a non-actionable follow-up to an open
   *  DoBox of the same person in another conversation, never create one. */
  openFollowUp?: boolean;
}

/**
 * Shared by all four chat platforms. No `platform` field: the step resolves
 * it at runtime from the first non-context message the LOAD_* step above
 * loaded (D10) — a config field here could silently drift from that load.
 */
export interface ProcessDoBoxStepConfig {
  includeResolved?: boolean;
  resolvedDaysBack?: string;
}

export interface CreateDoBoxStepConfig {
  subject: string; // Supports {{variable}} resolution
  description?: string; // Supports {{variable}} resolution
  taskGroupId?: string; // Falls back to user's default if omitted
  taskLabelIds?: string[];
  participantIds?: string[];
  priority?: InboxEntryPriority;
  dueAt?: number;
  isSuggestion?: boolean;
  /** Message IDs of source communications to link this DoBox to. When omitted,
   * the triggering communication (if any) is linked automatically. */
  communicationIds?: string[];
  entryKind?: DoBoxEntryKind;
  parentInboxEntryId?: string;
}

export interface ResolveDoBoxStepConfig {
  inboxEntryId: string; // Supports {{variable}} resolution
}

export interface AcceptDoBoxSuggestionStepConfig {
  inboxEntryId: string; // Supports {{variable}} resolution
}

export interface AddDoBoxCommentStepConfig {
  inboxEntryId: string; // Supports {{variable}} resolution
  comment: string; // Supports {{variable}} resolution
}

export interface UpdateDoBoxParticipantsStepConfig {
  inboxEntryId: string; // Supports {{variable}} resolution
  addParticipantIds?: string[];
  removeParticipantIds?: string[];
}

export interface UpdateDoBoxLabelsStepConfig {
  inboxEntryId: string; // Supports {{variable}} resolution
  addLabelIds?: string[];
  removeLabelIds?: string[];
}

export enum ValidateDoBoxRulesInputMode {
  COMMUNICATION = 'COMMUNICATION',
  COMMUNICATIONS_BATCH = 'COMMUNICATIONS_BATCH',
}

export interface ValidateDoBoxRulesStepConfig {
  source?: string;
  inputMode?: ValidateDoBoxRulesInputMode;
  communicationsInput?: string;
}

export const DOBOX_TRIGGER_KEYS = [
  TriggerKey.DOBOX_CREATED,
  TriggerKey.PARTICIPANT_ADDED,
  TriggerKey.LABEL_ADDED,
  TriggerKey.LABEL_REMOVED,
  TriggerKey.TASK_GROUP_CHANGED,
  TriggerKey.COMMENT_ADDED,
  TriggerKey.DOBOX_RESOLVED,
  TriggerKey.DOBOX_REOPENED,
  TriggerKey.DOBOX_APPROVED,
  TriggerKey.DOBOX_REJECTED,
];

/** The entry's version at the change; with `inboxEntryId`, `triggerKey` and the recipient it identifies this event. */
export interface VersionedDoBoxQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  version?: number;
}

export interface DoBoxCreatedQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.DOBOX_CREATED;
  inboxEntryId: string;
}

export interface CommentAddedQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.COMMENT_ADDED;
  inboxEntryId: string;
  commentId: string;
}

export interface ParticipantAddedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.PARTICIPANT_ADDED;
  inboxEntryId: string;
  addedParticipantIds: string[];
}

export interface LabelAddedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.LABEL_ADDED;
  inboxEntryId: string;
  addedLabelIds: string[];
}

export interface LabelRemovedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.LABEL_REMOVED;
  inboxEntryId: string;
  removedLabelIds: string[];
}

export interface TaskGroupChangedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.TASK_GROUP_CHANGED;
  inboxEntryId: string;
  newTaskGroupId: string;
}

export interface DoBoxResolvedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.DOBOX_RESOLVED;
  inboxEntryId: string;
}

export interface DoBoxReopenedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.DOBOX_REOPENED;
  inboxEntryId: string;
}

export interface DoBoxApprovedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.DOBOX_APPROVED;
  inboxEntryId: string;
  /** Optional: messages enqueued before this field shipped won't have it. */
  decidedByUserId?: string;
}

export interface DoBoxRejectedQueueBody extends VersionedDoBoxQueueBody {
  messageType: QueueMessageType.DOBOX_EVENT;
  triggerKey: TriggerKey.DOBOX_REJECTED;
  inboxEntryId: string;
  /** Optional: messages enqueued before this field shipped won't have it. */
  decidedByUserId?: string;
}

export type DoBoxEventQueueBody =
  | DoBoxCreatedQueueBody
  | CommentAddedQueueBody
  | ParticipantAddedQueueBody
  | LabelAddedQueueBody
  | LabelRemovedQueueBody
  | TaskGroupChangedQueueBody
  | DoBoxResolvedQueueBody
  | DoBoxReopenedQueueBody
  | DoBoxApprovedQueueBody
  | DoBoxRejectedQueueBody;

// Dobox trigger configs

export enum DoBoxTaskGroupScope {
  ALL = 'ALL',
  SELECTED = 'SELECTED',
}

export interface BaseDoboxTriggerConfig {
  taskGroupScope: DoBoxTaskGroupScope;
  selectedTaskGroupIds?: string[]; // required if taskGroupScope is 'SELECTED'
}

export interface CommentAddedConfig extends BaseDoboxTriggerConfig {
  selectedLabelIds?: string[];
}
